/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
import {
    isHydrated,
    getEnvStaleTime,
    getEnvRefetchOnMount,
    getPageDesignerRegion
} from '../../../utils/utils'
import {usePage} from '@salesforce/commerce-sdk-react'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {PDCHeaderTopBanner} from '../../../page-designer/assets'
import {HEADER_PDC_PAGE_ID, HEADER_OUTLET_PDC_PAGE_ID} from '../../header/constants'

import {usePageParameters} from '@app/hooks/use-page-parameters'
import useSiteType from '@app/hooks/use-site-type'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcHeaderTopBanner': PDCHeaderTopBanner
}

/**
 * AboveHeader component to render the above header banner
 * @returns {React.ReactElement}
 */
const AboveHeader = () => {
    const staleTime = getEnvStaleTime()
    const refetchOnMount = getEnvRefetchOnMount()
    const {isOutlet} = useSiteType()

    const parameters = usePageParameters(
        isOutlet ? HEADER_OUTLET_PDC_PAGE_ID : HEADER_PDC_PAGE_ID,
        {includeCountry: true}
    )
    const {data: headerPage, isFetchedAfterMount} = usePage(
        {parameters},
        {staleTime, refetchOnMount},
        {enabled: Boolean(parameters?.c_country)}
    )

    const headerTopBannerComponent = getPageDesignerRegion(headerPage, 'main')?.components?.find(
        (comp) => comp?.typeId === 'commerce_assets.pdcHeaderTopBanner'
    )
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})
    const [showBanner, setShowBanner] = useState(headerTopBannerComponent !== undefined)

    useEffect(() => {
        if (!headerTopBannerComponent) return

        const hideBannerOnScroll = () => {
            setShowBanner(window.scrollY === 0)
        }
        hideBannerOnScroll()
        window.addEventListener('scroll', hideBannerOnScroll)

        return () => {
            window.removeEventListener('scroll', hideBannerOnScroll)
        }
    }, [headerTopBannerComponent])

    /**
     * Stale data fix.
     * This component's content is rendered on the server with stale data.
     * To avoid flashes of incorrect data, we show the content after fresh data is fetched on the client: isFetchedAfterMount.
     * If refetchOnMount is not 'always', then use the default behavior.
     */
    const staleDataFix =
        (refetchOnMount === 'always' && isFetchedAfterMount) || refetchOnMount !== 'always'

    return (
        staleDataFix &&
        showBanner &&
        headerTopBannerComponent && (
            <PageContext.Provider value={contextValue}>
                <Component component={headerTopBannerComponent} />
            </PageContext.Provider>
        )
    )
}

export default AboveHeader
